import Button from '@components/v2/ui/Button/Button';
import Icon from '@components/v2/ui/Icon/Icon';
import Menu from '@components/v2/ui/Menu/Menu';
import { PopoverActions } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { useRef, useState } from 'react';
import DisabledButtonWithTooltip from './components/DisabledButtonWithTooltip';
import { useButtonBaseWithSelectionMenuStyles } from './styles/useButtonBaseWithSelectionMenu.styles';
import {
  ButtonBaseWithSelectionMenuProps,
  SelectionMenuViewMode,
} from './types/buttonBaseWithSelectionMenu.types';

const MENU_VIEWPORT_MARGIN = 32;

const MENU_GUTTER = 0.75;

const ButtonBaseWithSelectionMenu = ({
  disabled,
  viewMode,
  renderMenuContent,
  buttonContent,
  isLoadingButton,
  disabledTooltipLabel,
  isFocusable = false,
  menuConfig,
  onClickOpen,
  onClose,
  testId,
}: ButtonBaseWithSelectionMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuActions = useRef<PopoverActions | null>(null);
  const { classes, cx } = useButtonBaseWithSelectionMenuStyles();
  const disabledButton = disabled || isLoadingButton || viewMode === SelectionMenuViewMode.VIEW;
  const isMenuOpen = Boolean(anchorEl);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    onClickOpen?.(event);
  };

  const handleCLose = () => {
    setAnchorEl(null);
    onClose?.();
  };

  if (disabledButton && disabledTooltipLabel) {
    return (
      <DisabledButtonWithTooltip
        testId={testId}
        buttonContent={buttonContent}
        disabledTooltipLabel={disabledTooltipLabel}
      />
    );
  }

  return (
    <>
      {!disabledButton && (
        <Menu
          action={menuActions}
          onClose={handleCLose}
          open={isMenuOpen}
          anchorEl={anchorEl}
          className={classes.menu}
          disableAutoFocusItem={menuConfig?.hasSearch}
          slotProps={{
            list: {
              disablePadding: true,
              sx: menuConfig && {
                padding: 0,
                maxHeight: menuConfig.maxHeight,
                overflowY: 'auto',
              },
              ...(menuConfig?.hasSearch && { autoFocus: false }),
            },
            paper: menuConfig && {
              sx: {
                maxWidth: `min(${menuConfig.maxWidth}px, calc(100vw - ${MENU_VIEWPORT_MARGIN}px))`,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                padding: MENU_GUTTER,
              },
            },
            transition: {
              onEntering: (node) => {
                const anchorWidth = `${anchorEl?.clientWidth}px`;

                if (menuConfig) {
                  node.style.minWidth = anchorWidth;
                  return;
                }

                node.style.width = anchorWidth;
              },
            },
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {renderMenuContent({
            closeMenu: handleCLose,
            anchorEl,
            updatePosition: () => menuActions.current?.updatePosition(),
          })}
        </Menu>
      )}
      <Button
        fullWidth
        size="small"
        variant="contained"
        color="secondary"
        isActive={isMenuOpen}
        aria-haspopup="menu"
        aria-expanded={isMenuOpen}
        tabIndex={isFocusable ? 0 : -1}
        testId={testId}
        onClick={handleOpenMenu}
        disabled={disabledButton}
        className={classes.button}
      >
        <div style={{ minWidth: 0, marginRight: 4, width: '100%' }}>{buttonContent}</div>

        <Icon
          icon={ChevronDown}
          size="sm"
          className={cx(classes.chevron, {
            [classes.chevronOpen]: isMenuOpen,
            [classes.chevronHidden]: disabledButton,
          })}
        />
      </Button>
    </>
  );
};

export default ButtonBaseWithSelectionMenu;
